/* Shared query model; neither adapter broadens enforced scope. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.UMCDiscoveryCore = api;
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const facets = ['region', 'topic', 'format', 'style', 'colour', 'period', 'creator', 'orientation', 'subject', 'art_style', 'place', 'place_type', 'creator_role'];
  const auxiliary = ['view', 'preview_theme_id'];
  const keyPattern = /^[a-zA-Z0-9_.:-]{1,150}$/;
  function normalize(params, keys = facets) {
    if (params.toString().length > 6000) throw new Error('Query is too long');
    const allowed = new Set([...keys, 'sort_by', 'page', 'year_min', 'year_max', ...auxiliary]);
    for (const key of params.keys()) if (!allowed.has(key) && !key.startsWith('utm_')) throw new Error('Unsupported query parameter: ' + key);
    const filters = {};
    for (const key of keys) {
      const values = [...new Set(params.getAll(key).filter(Boolean))].sort();
      if (values.length > 20 || values.some(value => value.length > 200)) throw new Error('Invalid filter');
      if (values.length) filters[key] = values;
    }
    for (const key of ['page', 'sort_by', 'year_min', 'year_max']) if (params.getAll(key).length > 1) throw new Error('Duplicate scalar parameter');
    const page = params.get('page') || '1';
    if (!/^[1-9][0-9]{0,6}$/.test(page)) throw new Error('Invalid page');
    const state = { filters, sort: params.get('sort_by') || '', page: Number(page) };
    for (const key of ['year_min', 'year_max']) {
      const value = params.get(key);
      if (value !== null && value !== '') {
        if (!/^[1-9][0-9]{0,3}$/.test(value)) throw new Error('Invalid publication year');
        state[key] = Number(value);
      }
    }
    if (state.year_min && state.year_max && state.year_min > state.year_max) throw new Error('Invalid year range');
    return state;
  }
  function serialize(state, previous = new URLSearchParams()) {
    const params = new URLSearchParams();
    for (const key of auxiliary) if (previous.has(key)) params.set(key, previous.get(key));
    for (const key of Object.keys(state.filters).sort()) {
      if (!keyPattern.test(key)) throw new Error('Invalid parameter');
      for (const value of state.filters[key]) params.append(key, value);
    }
    if (state.sort) params.set('sort_by', state.sort);
    if (state.page > 1) params.set('page', state.page);
    for (const key of ['year_min', 'year_max']) if (state[key]) params.set(key, state[key]);
    return params;
  }
  function refine(state, key, values) {
    const next = { ...state, filters: { ...state.filters }, page: 1 };
    if (key === 'sort_by') next.sort = values[0] || '';
    else if (key === 'year_min' || key === 'year_max') { delete next[key]; if (values[0]) next[key] = Number(values[0]); }
    else if (values.length) next.filters[key] = [...new Set(values)].sort();
    else delete next.filters[key];
    return next;
  }
  function clear(state) { return { filters: {}, sort: state.sort, page: 1 }; }
  function toNative(params, nativeFilters, categories) {
    const result = new URLSearchParams(params);
    for (const key of facets) {
      if (!params.has(key)) continue;
      const configured = categories[key];
      if (!configured) throw new Error('Friendly filter is not configured: ' + key);
      const filter = nativeFilters.find(item => item.key === configured.param);
      if (!filter) throw new Error('Native filter is unavailable: ' + key);
      result.delete(key);
      for (const value of params.getAll(key)) {
        const option = configured.values.find(item => item.key === value);
        if (!option || !filter.values.some(item => item.value === option.value)) throw new Error('Unknown filter value: ' + value);
        result.append(filter.key, option.value);
      }
    }
    if (params.has('year_min') || params.has('year_max')) throw new Error('Native arbitrary year ranges are unavailable');
    return result;
  }
  function validateResponse(data, config) {
    if (!data || data.schema_version !== 1 || data.status !== 'ok') throw new Error('Invalid discovery response');
    if (!data.scope || (data.scope.collection || '') !== (config.collection || '') || (data.scope.saved_key || '') !== (config.savedKey || '')) throw new Error('Result scope does not match this page');
    if (!data.context || ['country', 'market', 'language', 'currency'].some(key => data.context[key] !== config.context[key])) throw new Error('Result context does not match this visitor');
    if (!data.index_version || !Number.isSafeInteger(data.total?.value) || data.total.value < 0 || typeof data.total.exact !== 'boolean') throw new Error('Invalid count');
    if (!data.query || !Number.isSafeInteger(data.query.page) || typeof data.query.sort !== 'string' || !data.query.filters || !Array.isArray(data.facets) || !Array.isArray(data.results) || data.results.length > 24 || !data.capabilities || !Array.isArray(data.capabilities.sorts)) throw new Error('Invalid results');
    if (!Number.isSafeInteger(data.pagination?.page) || data.pagination.page < 1 || data.pagination.page_size !== 24 || !Number.isSafeInteger(data.pagination.pages) || data.pagination.pages < 0) throw new Error('Invalid pagination');
    for (const [key, group] of Object.entries(data.query.filters)) if (!facets.includes(key) || !Array.isArray(group) || group.some(value => typeof value !== 'string')) throw new Error('Invalid effective query');
    if (data.pagination.page !== data.query.page || data.results.length > data.total.value) throw new Error('Inconsistent results');
    const ids = new Set();
    for (const product of data.results) {
      if (!product.id || ids.has(product.id) || (!data.development_fixture && !/^[a-z0-9][a-z0-9-]{0,254}$/.test(product.handle))) throw new Error('Invalid parent products');
      ids.add(product.id);
    }
    for (const facet of data.facets) {
      if (!facets.includes(facet.key) || !['AND', 'OR'].includes(facet.operator) || !Array.isArray(facet.values)) throw new Error('Invalid facet');
      for (const value of facet.values) if (typeof value.key !== 'string' || typeof value.label !== 'string' || typeof value.selected !== 'boolean' || !Number.isSafeInteger(value.count) || value.count < 0) throw new Error('Invalid facet value');
    }
    if (data.development_fixture && config.source !== 'fixture') throw new Error('Fixtures cannot serve live discovery');
    return data;
  }
  // Request sequence protects the entire fetch + card-render boundary, even if a transport ignores abort.
  class LatestRequest {
    constructor() { this.sequence = 0; }
    begin() { this.controller?.abort(); this.controller = new AbortController(); return { id: ++this.sequence, signal: this.controller.signal }; }
    current(request) { return request.id === this.sequence && !request.signal.aborted; }
    cancel() { this.controller?.abort(); this.sequence++; }
  }
  return { facets, normalize, serialize, refine, clear, toNative, validateResponse, LatestRequest };
});
